// 대화의 순수 규칙 — 인메모리·Supabase·화면·알림이 같은 한 벌을 쓴다.

import { formatNumber } from "../../lib/format";
import {
  type LiveMessagePhoto,
  MAX_AUTHOR_NAME,
  MAX_MESSAGE_BODY,
  type MessageAuthorKind,
  type MessageItem,
  type MessageThread,
  type MessageViewer,
  type ProjectMessage,
} from "./types";

export const MESSAGE_EMPTY_MESSAGE = "내용을 적어 주세요";
export const MESSAGE_TOO_LONG_MESSAGE = `${formatNumber(MAX_MESSAGE_BODY)}자까지 적을 수 있습니다`;
export const MESSAGE_CLOSED_MESSAGE = "완료된 현장의 대화는 읽기만 할 수 있습니다";
export const MESSAGE_RATE_MESSAGE = "잠시 뒤 다시 보내 주세요";
export const MESSAGE_UNAVAILABLE_MESSAGE = "지금은 보낼 수 없습니다. 잠시 뒤 다시 보내 주세요";
export const MESSAGE_AUTHOR_NAME_MESSAGE = `작성자 이름은 1~${MAX_AUTHOR_NAME}자여야 합니다`;
export const MESSAGE_NOT_FOUND_MESSAGE = "글을 찾을 수 없습니다";
export const MESSAGE_NOT_MINE_MESSAGE = "내가 쓴 글만 고치거나 지울 수 있습니다";
export const MESSAGE_DELETED_MESSAGE = "이미 지운 글입니다";
export const MESSAGE_STALE_MESSAGE = "다른 곳에서 바뀌었습니다 — 새로 불러와 주세요";
export const MESSAGE_DELETED_LABEL = "삭제된 메시지입니다";
export const MESSAGE_EDITED_LABEL = "수정됨";
export const MESSAGE_PHOTO_EXCERPT = "(사진)";
// `0120` project_message_change_guard 위반 — 인메모리·Supabase 가 같은 문구를 쓴다.
export const MESSAGE_CHANGE_GUARD_MESSAGE = "이 글은 그렇게 고칠 수 없습니다";

export type NormalizedBody = { ok: true; body: string } | { ok: false; message: string };

// 줄바꿈은 LF 로 · 앞뒤 공백은 뺀다 · 글자 수는 코드포인트(DB char_length 와 같은 자).
export const normalizeMessageBody = (raw: unknown): NormalizedBody => {
  const body = (typeof raw === "string" ? raw : "").replace(/\r\n?/g, "\n").trim();
  if (body === "") return { ok: false, message: MESSAGE_EMPTY_MESSAGE };
  if (Array.from(body).length > MAX_MESSAGE_BODY) return { ok: false, message: MESSAGE_TOO_LONG_MESSAGE };
  return { ok: true, body };
};

// 사진이 붙은 글은 본문이 비어도 된다(`0120` project_message_body_len 과 같은 규칙).
export const normalizeMessageContent = (raw: unknown, hasPhoto: boolean): NormalizedBody => {
  const normalized = normalizeMessageBody(raw);
  if (!normalized.ok && hasPhoto && normalized.message === MESSAGE_EMPTY_MESSAGE) return { ok: true, body: "" };
  return normalized;
};

// 고치거나 지울 수 있는지 — 직원은 자기 멤버 id 로 쓴 글만, 고객은 고객 글만 · 지운 글은 다시 못 고친다 · 화면이 본 뒤 바뀐 글은 새로 불러온다.
export type MessageChangeBlock = "not_mine" | "deleted" | "stale";

export const isOwnMessage = (message: Pick<ProjectMessage, "author_kind" | "author_member_id">, viewer: MessageViewer): boolean =>
  viewer.kind === "STAFF"
    ? message.author_kind === "STAFF" && message.author_member_id !== null && message.author_member_id === viewer.member_id
    : message.author_kind === "CUSTOMER";

export const messageChangeBlockOf = (
  message: Pick<ProjectMessage, "author_kind" | "author_member_id" | "deleted_at" | "edited_at">,
  viewer: MessageViewer,
  seen: string | null
): MessageChangeBlock | null => {
  if (!isOwnMessage(message, viewer)) return "not_mine";
  if (message.deleted_at !== null) return "deleted";
  if (!isMessageVersionSeen(message, seen)) return "stale";
  return null;
};

// 지운 글의 모양 — 본문을 비우고 지운 시각을 남긴다 · 사진 열은 청소가 바이트를 지운 뒤 비운다(`0120` body_len·트리거와 같은 모양).
export const deletedMessageOf = (message: ProjectMessage, at: string): ProjectMessage => ({ ...message, body: "", deleted_at: at });

// 화면·바이트 경로가 볼 사진 — 지운 글은 경로가 남아 있어도 사진이 없다.
export const livePhotoPathOf = (message: Pick<ProjectMessage, "deleted_at" | "photo_path">): string | null =>
  message.deleted_at === null ? message.photo_path : null;

// 바이트 저장이 실패한 사진 글 되돌리기 대상 — 살아 있고 그 경로를 가리키는 글만(완료 현장도 · `0120` 트리거의 service_role 분기와 한 벌).
export const isPhotoDiscardTarget = (message: Pick<ProjectMessage, "deleted_at" | "photo_path">, path: string): boolean =>
  message.deleted_at === null && message.photo_path === path;

// 바이트 확인 표시를 찍을 글 — 살아 있고 사진이 있고 아직 안 찍은 글만(`0120` rpc_mark_message_photos_checked·트리거와 한 벌).
export const isPhotoCheckTarget = (message: Pick<ProjectMessage, "deleted_at" | "photo_path" | "photo_checked_at">): boolean =>
  message.deleted_at === null && message.photo_path !== null && message.photo_checked_at === null;

// 고아 청소의 카나리 — 바이트를 확인한 산 사진 글(이게 「없음」이면 저장소 고장 신호).
export const isPhotoCanary = (message: Pick<ProjectMessage, "deleted_at" | "photo_path" | "photo_checked_at">): boolean =>
  message.deleted_at === null && message.photo_path !== null && message.photo_checked_at !== null;

// 고아 청소는 「없음」을 하루 1회 cron 두 번에 걸쳐 봐야 치운다 — 24시간이 아니라 23시간인 건 다음 날 실행이 조금 일찍 돌아도(앞 단계 길이·cron 지터) 48시간으로 밀리지 않게.
export const MESSAGE_PHOTO_MISSING_GRACE_MS = 23 * 60 * 60 * 1000;

type PhotoSweepRow = Pick<ProjectMessage, "deleted_at" | "photo_path" | "photo_checked_at" | "photo_missing_at">;

// btrim 처럼 공백(0x20)만 깎고 글자가 남는지(`0120` project_message_body_len 과 한 벌).
const hasMessageBody = (body: string): boolean => body.replace(/^ +| +$/g, "") !== "";

// 바이트 없음 표시를 찍을 글 — 확인 대상이고 아직 표시가 없는 글만(`0120` rpc_mark_message_photos_missing·트리거와 한 벌).
export const isPhotoMissingMarkTarget = (message: PhotoSweepRow): boolean =>
  isPhotoCheckTarget(message) && message.photo_missing_at === null;

// 사진만 뗄 글 — 없음 표시가 있는 확인 대상 · 그 경로 · 본문이 남는 글만(`0120` rpc_detach_message_photo·트리거와 한 벌).
export const isPhotoDetachTarget = (message: PhotoSweepRow & Pick<ProjectMessage, "body">, path: string): boolean =>
  isPhotoCheckTarget(message) && message.photo_missing_at !== null && message.photo_path === path && hasMessageBody(message.body);

// 고아 청소가 지울 사진만 있는 글 — 없음 표시가 있는 확인 대상 · 그 경로 · 빈 본문만(`0120` rpc_discard_orphan_message_photo 와 한 벌 · 그 사이 확인된 글은 안 지운다).
export const isPhotoOrphanDiscardTarget = (message: PhotoSweepRow & Pick<ProjectMessage, "body">, path: string): boolean =>
  isPhotoCheckTarget(message) && message.photo_missing_at !== null && message.photo_path === path && !hasMessageBody(message.body);

// 사진만 뗀 글 — 사진 셋과 없음 표시를 비우고 나머지는 그대로.
export const photoDetachedOf = (message: ProjectMessage): ProjectMessage => ({
  ...message,
  photo_path: null,
  photo_mime: null,
  photo_size_bytes: null,
  photo_missing_at: null,
});

export type OrphanPhotoAction = "mark" | "wait" | "detach" | "discard";

// 바이트 「없음」 글의 다음 할 일 — 처음이면 표시 · 유예(23시간) 안이면 기다림 · 지나면 본문 있는 글은 사진만 떼고 사진만 있는 글은 지운다.
export const orphanPhotoActionOf = (row: Pick<LiveMessagePhoto, "body" | "photo_missing_at">, now: Date): OrphanPhotoAction => {
  if (row.photo_missing_at === null) return "mark";
  if (now.getTime() * 1000 - instantMicros(row.photo_missing_at) < MESSAGE_PHOTO_MISSING_GRACE_MS * 1000) return "wait";
  return hasMessageBody(row.body) ? "detach" : "discard";
};

// 화면용 글 — 고객 응답에 저장 경로가 실리지 않게 photo_* 를 빼고 사진 있음만 남긴다.
export const messageItemOf = (message: ProjectMessage): MessageItem => {
  const { photo_path: _path, photo_mime: _mime, photo_size_bytes: _size, photo_checked_at: _checked, photo_missing_at: _missing, ...rest } = message;
  return { ...rest, has_photo: livePhotoPathOf(message) !== null };
};

// 알림 미리보기 — 사진만 보낸 글은 「(사진)」.
export const messageExcerptOf = (message: Pick<ProjectMessage, "body" | "photo_path" | "deleted_at">): string =>
  message.body !== "" ? message.body : livePhotoPathOf(message) ? MESSAGE_PHOTO_EXCERPT : "";

const ISO_PARTS = /^(.+T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

// 마이크로초 정수(UTC) — PostgREST 는 소수 6자리를 주는데 Date.parse 는 ms 에서 잘라 같은 ms 의 두 글을 같다고 본다.
export const instantMicros = (iso: string): number => {
  const m = ISO_PARTS.exec(iso.trim());
  if (!m) return Date.parse(iso) * 1000;
  const frac = (m[2] ?? "").padEnd(6, "0").slice(0, 6);
  const zone = (m[3] ?? "Z").replace(/^([+-]\d{2}):?(\d{2})?$/, (_, h: string, mm?: string) => `${h}:${mm ?? "00"}`);
  const ms = Date.parse(`${m[1]}.${frac.slice(0, 3)}${zone}`);
  return ms * 1000 + Number(frac.slice(3));
};

const instant = instantMicros;

// 오래된 것부터 — 같은 시각이면 id 로 고정한다.
export const compareMessages = (a: ProjectMessage, b: ProjectMessage): number =>
  instant(a.created_at) - instant(b.created_at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const sortMessages = (messages: readonly ProjectMessage[]): ProjectMessage[] =>
  [...messages].sort(compareMessages);

const isAfter = (message: ProjectMessage, readAt: string | null): boolean =>
  readAt === null || instant(message.created_at) > instant(readAt);

const isLive = (message: ProjectMessage): boolean => message.deleted_at === null;

// 수정 시각 — 앞 시각(수정·작성)보다 반드시 뒤로 민다(버전 대조 열이라 같은 값이 다시 나오면 안 된다 · `0120` 트리거는 1µs, 인메모리 시계는 1ms).
export const editedAtOf = (message: Pick<ProjectMessage, "created_at" | "edited_at">, now: string): string => {
  const floor = instant(message.edited_at ?? message.created_at);
  return instant(now) > floor ? now : new Date(Math.floor(floor / 1000) + 1).toISOString();
};

// 화면이 본 버전(edited_at)이 지금 글과 같은지 — 다르면 다른 곳에서 고쳤다(MessageStaleError).
export const isMessageVersionSeen = (message: Pick<ProjectMessage, "edited_at">, seen: string | null): boolean =>
  message.edited_at === null || seen === null ? message.edited_at === seen : instant(message.edited_at) === instant(seen);

const IDENTITY_COLUMNS = ["id", "org_id", "project_id", "author_kind", "author_member_id", "author_name", "created_at"] as const;
const PHOTO_COLUMNS = ["photo_path", "photo_mime", "photo_size_bytes"] as const;

// 지운 글에서 사진 셋만 비우는 청소 갱신인지(`0120` 트리거의 old.deleted_at 분기와 한 벌).
const isPhotoSweep = (old: ProjectMessage, next: ProjectMessage): boolean =>
  old.photo_path !== null &&
  PHOTO_COLUMNS.every((c) => next[c] === null) &&
  (Object.keys(old) as (keyof ProjectMessage)[]).every((c) => (PHOTO_COLUMNS as readonly string[]).includes(c) || next[c] === old[c]);

// `0120` project_message_change_guard 의 update 규칙 한 벌 — 누가·어디·언제·사진 그대로 · 지우기는 한 번 · 지운 글은 청소만(시각은 stampMessageChange 가 찍는다).
export const isMessageChangeAllowed = (old: ProjectMessage, next: ProjectMessage): boolean => {
  if (old.deleted_at !== null) return isPhotoSweep(old, next);
  if (IDENTITY_COLUMNS.some((c) => next[c] !== old[c])) return false;
  return PHOTO_COLUMNS.every((c) => next[c] === old[c]) && next.photo_checked_at === old.photo_checked_at && next.photo_missing_at === old.photo_missing_at;
};

// 시각은 쓰는 쪽이 아니라 저장소 시계로 찍는다(`0120` 트리거와 같은 뜻) — 보낸 edited_at·deleted_at 은 버리고 지우면 본문을 비운다.
export const stampMessageChange = (old: ProjectMessage, next: ProjectMessage, now: string): ProjectMessage => {
  if (old.deleted_at !== null) return next;
  if (next.deleted_at !== null) return { ...next, body: "", edited_at: old.edited_at, deleted_at: now };
  return { ...next, edited_at: next.body !== old.body ? editedAtOf(old, now) : old.edited_at };
};

const otherSide = (side: MessageAuthorKind): MessageAuthorKind => (side === "STAFF" ? "CUSTOMER" : "STAFF");

// 보는 쪽이 아직 안 읽은 상대 글 수 — 자기 글은 세지 않는다.
export const unreadCountOf = (
  messages: readonly ProjectMessage[],
  viewer: MessageAuthorKind,
  readAt: string | null
): number => messages.filter((m) => isLive(m) && m.author_kind === otherSide(viewer) && isAfter(m, readAt)).length;

export const staffUnreadOf = (thread: MessageThread): number =>
  unreadCountOf(thread.messages, "STAFF", thread.staff_read_at);

export const customerUnreadOf = (thread: MessageThread): number =>
  unreadCountOf(thread.messages, "CUSTOMER", thread.customer_read_at);

// 지금 본 마지막 글의 시각이 읽은 시각보다 앞설 때만 옮긴다(아니면 null — 쓸 필요 없음).
export const nextReadAtOf = (messages: readonly ProjectMessage[], readAt: string | null): string | null => {
  const last = sortMessages(messages).at(-1);
  if (!last) return null;
  return readAt === null || instant(last.created_at) > instant(readAt) ? last.created_at : null;
};

// 읽은 시각은 그 현장의 마지막 글보다 앞설 수 없다(글이 없으면 지금) — `0038` 트리거와 같은 규칙.
export const clampReadAt = (messages: readonly ProjectMessage[], readAt: string, now: string): string => {
  const last = sortMessages(messages).at(-1)?.created_at ?? now;
  return instant(readAt) > instant(last) ? last : readAt;
};

// 두 읽은 시각 중 늦은 쪽 — 겹쳐 써도 뒤로 가지 않는다(`0037` greatest).
export const laterReadAt = (current: string | null, next: string): string =>
  current !== null && instant(current) >= instant(next) ? current : next;

// 이 시각까지 본 글만 — 화면을 그린 뒤 들어온 글은 읽음으로 치지 않는다.
export const messagesSeenUntil = (messages: readonly ProjectMessage[], seenUntil: string): ProjectMessage[] =>
  messages.filter((m) => instant(m.created_at) <= instant(seenUntil));

// 고객이 마지막으로 읽은 뒤 첫 직원 글인지 — 둘째부터는 알리지 않는다(폭탄 방지).
export const isFirstStaffSinceRead = (
  messages: readonly ProjectMessage[],
  messageId: string,
  customerReadAt: string | null
): boolean =>
  sortMessages(messages).find((m) => isLive(m) && m.author_kind === "STAFF" && isAfter(m, customerReadAt))?.id === messageId;

// 알림 중복 키의 원천 — 고객이 읽을 때마다 새 주기가 열린다.
export const messageNotifySource = (projectId: string, customerReadAt: string | null): string =>
  `${projectId}:${customerReadAt ?? "never"}`;

// 완료 현장은 양쪽 모두 읽기만 한다.
export const isThreadClosed = (thread: Pick<MessageThread, "project_status">): boolean =>
  thread.project_status === "CLOSED";

// 목록 카드용 — 지운 글을 뺀 최근 n줄(오래된 것부터 · 미리보기에 「삭제된 메시지」 자리를 남기지 않는다).
export const recentMessagesOf = (messages: readonly ProjectMessage[], n: number): ProjectMessage[] =>
  sortMessages(messages.filter(isLive)).slice(-n);

// `0037` project_message_author_name_len 과 한 벌 — btrim 은 공백(0x20)만 깎는다.
export const isAuthorNameOk = (name: string): boolean => {
  const len = Array.from(name.replace(/^ +| +$/g, "")).length;
  return len >= 1 && len <= MAX_AUTHOR_NAME;
};

// 고객 글의 이름 스냅샷 — 계약서의 고객 이름(비었으면 「고객」 · `0037` 의 100자 상한).
export const customerAuthorNameOf = (clientName: string | null | undefined): string =>
  Array.from((clientName ?? "").trim() || "고객").slice(0, MAX_AUTHOR_NAME).join("");
